import AddIcon from '@mui/icons-material/Add';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import TuneIcon from '@mui/icons-material/Tune';
import ViewListIcon from '@mui/icons-material/ViewList';
import {
  Badge, Checkbox, FormControl, FormControlLabel, FormGroup, IconButton, InputAdornment, InputLabel, ListItemButton, ListItemText, MenuItem, OutlinedInput, Popover, Select, Toolbar, Tooltip,
} from '@mui/material';
import { makeStyles, useTheme } from '@mui/styles';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useDeviceReadonly } from '../common/util/permissions';
import RaceRow from './RaceRow';

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  filterPanel: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    gap: theme.spacing(2),
    width: theme.dimensions.drawerWidthTablet,
  },
}));

const RacesToolbar = ({
  filteredGroups,
  groupsOpen,
  setGroupsOpen,
  keyword,
  setKeyword,
  filter,
  setFilter,
  filterSort,
  setFilterSort,
  filterMap,
  setFilterMap,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const t = useTranslation();

  const geofenceReadonly = useDeviceReadonly();

  const groups = useSelector((state) => state.groups.items);

  const toolbarRef = useRef();
  const inputRef = useRef();
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [groupsAnchorEl, setGroupsAnchorEl] = useState(null);

  const geofenceStatusCount = (status) => Object.values(groups).filter((d) => d.status === status).length;

  return (
    <Toolbar ref={toolbarRef} className={classes.toolbar}>
      <IconButton edge="start" onClick={() => setGroupsOpen(!groupsOpen)}>
        {groupsOpen ? <SportsScoreIcon /> : <ViewListIcon />}
      </IconButton>
      <OutlinedInput
        ref={inputRef}
        placeholder={t('sharedSearchGroups')}
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        onFocus={() => setGroupsAnchorEl(toolbarRef.current)}
        onBlur={() => setGroupsAnchorEl(null)}
        endAdornment={(
          <InputAdornment position="end">
            <IconButton size="small" edge="end" onClick={() => setFilterAnchorEl(inputRef.current)}>
              <Badge color="info" variant="dot" invisible={!filter.statuses.length && !filter.groups.length}>
                <TuneIcon fontSize="small" />
              </Badge>
            </IconButton>
          </InputAdornment>
      )}
        size="small"
        fullWidth
      />
      <Popover
        open={!!groupsAnchorEl && !groupsOpen}
        anchorEl={groupsAnchorEl}
        onClose={() => setGroupsAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: Number(theme.spacing(2).slice(0, -2)),
        }}
        marginThreshold={0}
        PaperProps={{
          style: { width: `calc(${toolbarRef.current?.clientWidth}px - ${theme.spacing(4)})` },
        }}
        elevation={1}
        disableAutoFocus
        disableEnforceFocus
      >
        {filteredGroups.slice(0, 3).map((_, index) => (
          <RaceRow key={filteredGroups[index].id} data={filteredGroups} index={index} />
        ))}
        {filteredGroups.length > 3 && (
        <ListItemButton alignItems="center" onClick={() => setGroupsOpen(true)}>
          <ListItemText
            primary={t('notificationAlways')}
            style={{ textAlign: 'center' }}
          />
        </ListItemButton>
        )}
      </Popover>
      <Popover
        open={!!filterAnchorEl}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <div className={classes.filterPanel}>
          <FormControl>
            <InputLabel>{t('geofenceStatus')}</InputLabel>
            <Select
              label={t('geofenceStatus')}
              value={filter.statuses}
              onChange={(e) => setFilter({ ...filter, statuses: e.target.value })}
              multiple
            >
              <MenuItem value="online">{`${t('geofenceStatusOnline')} (${geofenceStatusCount('online')})`}</MenuItem>
              <MenuItem value="offline">{`${t('geofenceStatusOffline')} (${geofenceStatusCount('offline')})`}</MenuItem>
              <MenuItem value="unknown">{`${t('geofenceStatusUnknown')} (${geofenceStatusCount('unknown')})`}</MenuItem>
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel>{t('settingsGroups')}</InputLabel>
            <Select
              label={t('settingsGroups')}
              value={filter.groups}
              onChange={(e) => setFilter({ ...filter, groups: e.target.value })}
              multiple
            >
              {Object.values(groups).sort((a, b) => a.name.localeCompare(b.name)).map((group) => (
                <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel>{t('sharedSortBy')}</InputLabel>
            <Select
              label={t('sharedSortBy')}
              value={filterSort}
              onChange={(e) => setFilterSort(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">{'\u00a0'}</MenuItem>
              <MenuItem value="name">{t('sharedName')}</MenuItem>
              <MenuItem value="lastUpdate">{t('geofenceLastUpdate')}</MenuItem>
            </Select>
          </FormControl>
          <FormGroup>
            <FormControlLabel
              control={<Checkbox checked={filterMap} onChange={(e) => setFilterMap(e.target.checked)} />}
              label={t('sharedFilterMap')}
            />
          </FormGroup>
        </div>
      </Popover>
      <IconButton edge="end" onClick={() => navigate('/settings/geofence')} disabled={geofenceReadonly}>
        <Tooltip open={!geofenceReadonly && Object.keys(groups).length === 0} title={t('geofenceRegisterFirst')} arrow>
          <AddIcon />
        </Tooltip>
      </IconButton>
    </Toolbar>
  );
};

export default RacesToolbar;
